import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/shared/PhoneInput';
import type { ManagerInput } from '@jobos/shared/types/manager';

const managerFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
}).refine(
  (data) => data.firstName || data.lastName,
  { message: 'At least one of first name or last name is required', path: ['lastName'] }
);

interface ManagerFormProps {
  clientId: string;
  onSubmit: (data: ManagerInput) => void | Promise<void>;
  defaultValues?: Partial<ManagerInput>;
  isPending?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

export function ManagerForm({
  clientId,
  onSubmit,
  defaultValues,
  isPending = false,
  submitButtonText = 'Save Manager',
  showCancelButton = true,
  onCancel,
}: ManagerFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<Omit<ManagerInput, 'clientId'>>({
    resolver: zodResolver(managerFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      ...defaultValues,
    },
  });

  const phone = watch('phone');

  // Load default values when they change (for edit mode)
  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (key !== 'clientId' && key !== 'name') {
          setValue(key as keyof Omit<ManagerInput, 'clientId'>, value || '');
        }
      });
    }
  }, [defaultValues, setValue]);

  const handleFormSubmit = (data: Omit<ManagerInput, 'clientId'>) => {
    // Transform empty strings to undefined for optional fields
    const cleanedData: ManagerInput = {
      clientId,
      firstName: data.firstName?.trim() || undefined,
      lastName: data.lastName?.trim() || undefined,
      email: data.email?.trim() || undefined,
      phone: data.phone?.trim() || undefined,
    };
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <Input {...register('firstName')} placeholder="First name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-600">*</span>
              </label>
              <Input {...register('lastName')} placeholder="Last name" />
              {errors.lastName && (
                <p className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              {...register('email')}
              type="email"
              placeholder="manager@example.com"
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <PhoneInput
              value={phone || ''}
              onChange={(value) => setValue('phone', value)}
              showLabel={true}
              label="Phone"
            />
          </div>
        </div>


      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4">
        {showCancelButton && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving...' : submitButtonText}
        </Button>
      </div>
    </form>
  );
}
